import React, { createContext, useState, useContext, useEffect, useMemo } from "react";
import axios from "axios";
import { getToken } from "../utils/tokenUtils";

// Create the InterviewsContext
const InterviewsContext = createContext();

// Custom hook to use the InterviewsContext
export const useInterviews = () => useContext(InterviewsContext);

// Provider component
export const InterviewsProvider = ({ children }) => {
  const [interviews, setInterviews] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    perPage: 10,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const API_URL = "https://hrmsnew.prepstripe.com/api/interviews"; // Replace with your actual API URL
  const API_URL2 = "https://hrmsnew.prepstripe.com/api/interviews/feedback"; // Replace with your actual API URL
  const token = getToken();

  // Fetch interviews from the API
  // const fetchInterviews = async (page = 1) => {
  //   setLoading(true);
  //   try {
  //     const response = await axios.get(API_URL, {
  //       headers: {
  //         Authorization: `Bearer ${token}`,
  //       },
  //     });
  //     // console.log("fetch interviewS:", response.data?.data?.data || [])
  //     setInterviews(response.data?.data?.data || []); // Assuming interviews are in `data` key
  //     setPagination({
  //       currentPage: response.current_page,
  //       totalPages: response.last_page,
  //       totalItems: response.total,
  //       perPage: response.per_page,
  //     });
  //     setError(null);
  //   } catch (error) {
  //     console.error("Error fetching interviews:", error);
  //     setError(error.response?.data?.message || "Failed to fetch interviews.");
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const fetchInterviews = async (page = 1) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}?page=${page}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
  
      await new Promise((r) => setTimeout(r, 1000));

      // setInterviews(response.data?.data?.data || []); // Assuming interviews are inside `data.data.data`
      setInterviews(response.data?.data?.data?.slice().reverse());
      setPagination({
        currentPage: response.data?.data?.current_page || 1,
        totalPages: response.data?.data?.last_page || 1,
        totalItems: response.data?.data?.total || 0,
        perPage: response.data?.data?.per_page || 10,
      });
      setError(null);
    } catch (error) {
      console.error("Error fetching interviews:", error);
      setError(error.response?.data?.message || "Failed to fetch interviews.");
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchInterviews();
  }, [API_URL, token]); // Re-fetch only if API_URL or token changes

  // Add a new interview
  const addInterview = async (newInterview) => {
    // console.log("newInterview payload: ", newInterview)
    try {
      const response = await axios.post(API_URL, newInterview, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      // console.log("Response after POST: ", response.data.data);

      // setInterviews((prev) => [...prev, response.data?.data]);
      // fetchInterviews()
      const interviewPayload = {
        ...response.data?.data?.interview,
        candidate: response.data?.data?.candidate,
      };
      
      // setInterviews((prev) => [...prev, interviewPayload]);
      setInterviews((prev) => [interviewPayload, ...prev]);


      return response.data.data;
      
    } catch (error) {
      console.error("Error adding interview:", error);
      setError(error.response?.data?.message || "Failed to add interview.");
      return error.response?.data;
    }
  };

  // Update an existing interview
  const updateInterview = async (updatedInterview) => {
    try {
      const response = await axios.put(
        `${API_URL}/${updatedInterview.id}`,
        updatedInterview,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      // setInterviews((prev) =>
      //   prev.map((interview) =>
      //     interview.id === updatedInterview.id ? response.data?.data : interview
      //   )
      // );
      fetchInterviews()
      return response.data.data;
    } catch (error) {
      console.error("Error updating interview:", error);
      setError(error.response?.data?.message || "Failed to update interview.");
      return error.response?.data;
    }
  };

  // Delete an interview
  const deleteInterview = async (id) => {
    try {
      await axios.delete(`${API_URL}/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setInterviews((prev) => prev.filter((interview) => interview.id !== id));
    } catch (error) {
      console.error("Error deleting interview:", error);
      setError(error.response?.data?.message || "Failed to delete interview.");
    }
  };

  const updateInterviewStatus = async (interviewId, feedback) => {
    // console.log("interviewid: ", interviewId, "status: ", feedback.status, "feedback: ", feedback.overallFeedback)
    try {
      const response = await axios.post(
        `${API_URL2}`,
        { id:interviewId,
          status: feedback.status,
          feedback: feedback.overallFeedback,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // Update the local state after a successful update
      // setInterviews((prevInterviews) =>
      //   prevInterviews.map((interview) =>
      //     interview.id === interviewId
      //       ? { ...interview, status: feedback.status }
      //       : interview
      //   )
      // );
      fetchInterviews()
      return response.data;
    } catch (error) {
      console.error("Error updating interview status:", error);
      // throw error;
      return error.response?.data;
    }
  };

  // Memoize the context value
  const value = useMemo(
    () => ({
      interviews,
      pagination,
      loading,
      error,
      addInterview,
      updateInterview,
      deleteInterview,
      fetchInterviews,
      updateInterviewStatus
    }),
    [interviews, loading, error] // Recalculate the value only when these dependencies change
  );

  return <InterviewsContext.Provider value={value}>{children}</InterviewsContext.Provider>;
};
